import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { ActionIntentsTable, ActionIntentDetailsSidebar } from '@/modules/promoted-blueprints';
import { useWorkflowBlueprints } from '@/hooks';

const TABLE_LABELS = {
  intentHeader: 'Workflow',
  subIntentsHeader: 'Scenarios',
  subIntentsSuffix: 'scenarios',
  enabledHeader: 'Promoted',
  emptyMessage: 'No workflows found.'
};

const SIDEBAR_LABELS = {
  intentLabel: 'Workflow',
  subIntentsTitle: 'Scenarios',
  noSubIntentsText: 'No scenarios for this workflow.'
};

/**
 * The promoted workflows by intent: what already serves from a precomputed answer. Protocols are promoted from
 * their own page (Live Promote); the by-protocol view and its back-testing switches are gone from here.
 */
export default function PromotedWorkflows() {
  const [searchParams] = useSearchParams();
  // A link from Promote names the blueprint to open, and opens it straight away.
  const [openBlueprintId, setOpenBlueprintId] = useState<string | null>(() => searchParams.get('blueprint'));
  const { data: workflowBlueprints, isLoading: workflowLoading } = useWorkflowBlueprints();

  return (
    <div className='flex h-full'>
      <div className='flex flex-1 flex-col gap-6 overflow-y-auto p-4'>
        <div className='space-y-1'>
          <h1 className='text-base font-semibold'>Promoted Protocols</h1>
          <p className='text-sm text-muted-foreground'>
            Precomputed answers the agent serves directly, by intent. Promote a workflow to serve it from the
            precomputed answer; turning it off hands the workflow back to the normal pipeline.
          </p>
        </div>

        <div className='flex flex-col gap-5'>
          <p className='rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground'>
            <span className='font-medium text-foreground'>Workflows</span> = multi-step actions.{' '}
            <span className='font-medium text-foreground'>Promote</span> a workflow to serve it from the
            precomputed answer (skips planning); turning it off hands the workflow back to the normal pipeline.
            Open a row to control each <span className='font-medium text-foreground'>named scenario</span>{' '}
            &mdash; a scenario can auto-send, or go out as a draft for review.
          </p>
          <ActionIntentsTable
            workflowBlueprints={workflowBlueprints ?? []}
            isLoading={workflowLoading}
            onRowClick={(blueprint) => setOpenBlueprintId(blueprint.id)}
            labels={TABLE_LABELS}
          />
        </div>
      </div>

      {openBlueprintId && (
        <ActionIntentDetailsSidebar
          blueprintId={openBlueprintId}
          onClose={() => setOpenBlueprintId(null)}
          labels={SIDEBAR_LABELS}
        />
      )}
    </div>
  );
}
